import React, { useState, useCallback, useRef, useLayoutEffect, useMemo, useEffect } from "react";
import { Row, Col, Button, Radio, TreeSelect, Pagination, Alert, Skeleton } from "antd"
import { CloseOutlined, ExclamationCircleFilled, MinusOutlined } from '@ant-design/icons';
import { Rnd } from "react-rnd";
import { useAtom } from "jotai";
import { isAllPredictionsPanelOpenState, infoModalSubstanceState, isSubstanceInfoModalOpenState, mapState, traceMapState, currentSerialState, loadingMapState } from "../stateManagement/commonState";
import styles from './AllPredictionsPanel.module.css';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { computeResults } from "../utils/results";

const buildKeyMap = (nodes, map = {}) => {
    for (const node of nodes) {
        map[node.value] = node;
        if (node.children) {
            buildKeyMap(node.children, map);
        }
    }
    return map;
}

const collectLeaves = (node) => {
    if (!node.children) {
        return [node.value];
    }
    return node.children.flatMap(collectLeaves);
}

const getSelectedLeaves = (keyMap, selectedKeys) => {
    return selectedKeys.flatMap(key => {
        const node = keyMap[key];
        if (!node) {
            console.warn(`key not found in tree: ${key}`);
            return [];
        }
        return collectLeaves(node);
    });
}

const getIQR = (signals) => {
    let q1 = 0, q2 = 0, q3 = 0;
    if (signals.length < 4) return { q1, q2, q3 };
    const sortedSignals = signals.sort((a, b) => a - b);
    if (sortedSignals.length % 2 !== 0) {
        q2 = sortedSignals[Math.floor(sortedSignals.length / 2)];
        const lower = sortedSignals.slice(0, sortedSignals.length / 2);
        const upper = sortedSignals.slice(sortedSignals.length / 2 + 1);
        if (lower.length % 2 !== 0) {
            q1 = lower[Math.floor(lower.length / 2)];
            q3 = upper[Math.floor(upper.length / 2)];
        } else {
            q1 = (lower[(lower.length / 2) - 1] + lower[(lower.length / 2)]) / 2;
            q3 = (upper[(upper.length / 2) - 1] + upper[(upper.length / 2)]) / 2;
        }
    } else {
        q2 = (sortedSignals[(sortedSignals.length / 2) - 1] + sortedSignals[(sortedSignals.length / 2)]) / 2;
        const lower = sortedSignals.slice(0, sortedSignals.length / 2);
        const upper = sortedSignals.slice(sortedSignals.length / 2);
        if (lower.length % 2 !== 0) {
            q1 = lower[Math.floor(lower.length / 2)];
            q3 = upper[Math.floor(upper.length / 2)];
        } else {
            q1 = (lower[(lower.length / 2) - 1] + lower[(lower.length / 2)]) / 2;
            q3 = (upper[(upper.length / 2) - 1] + upper[(upper.length / 2)]) / 2;
        }
    }
    return { q1, q2, q3 };
}

const treeData = [
    {
        title: 'Opioids',
        value: 'opioids',
        key: 'opioids',
        children: [
            {
                title: 'Fentanyl & analogues',
                value: 'opioids/fentanylAnalogs',
                key: 'opioids/fentanylAnalogs',
                children: [
                    { title: 'Acetylfentanyl', value: 'Acetylfentanyl', key: 'Acetylfentanyl' },
                    { title: 'Carfentanil', value: 'Carfentanil', key: 'Carfentanil' },
                    { title: 'Despropionyl fentanyl', value: 'Despropionyl fentanyl', key: 'Despropionyl fentanyl' },
                    { title: 'Fentanyl', value: 'Fentanyl', key: 'Fentanyl' },
                    { title: 'Furanylfentanyl', value: 'Furanylfentanyl', key: 'Furanylfentanyl' },
                    { title: 'Parafluorofentanyl', value: 'Parafluorofentanyl', key: 'Parafluorofentanyl' },
                    { title: 'Remifentanil', value: 'Remifentanil', key: 'Remifentanil' },
                ],
            },
            {
                title: 'Nitazene-class synthetics',
                value: 'opioids/nitazenes',
                key: 'opioids/nitazenes',
                children: [
                    { title: 'Isotonitazene', value: 'Isotonitazene', key: 'Isotonitazene' },
                    { title: 'Metonitazene', value: 'Metonitazene', key: 'Metonitazene' },
                    { title: 'Protonitazene', value: 'Protonitazene', key: 'Protonitazene' },
                ],
            },
            {
                title: 'Natural opiates',
                value: 'opioids/naturalOpiates',
                key: 'opioids/naturalOpiates',
                children: [
                    { title: 'Morphine', value: 'Morphine', key: 'Morphine' },
                    { title: 'Heroin', value: 'Heroin', key: 'Heroin' },
                ],
            },
            {
                title: 'Semi-synthetic opioids',
                value: 'opioids/semiSyntheticOpioids',
                key: 'opioids/semiSyntheticOpioids',
                children: [
                    { title: 'Hydrocodone', value: 'Hydrocodone', key: 'Hydrocodone' },
                    { title: 'Hydromorphone', value: 'Hydromorphone', key: 'Hydromorphone' },
                    { title: 'Oxycodone', value: 'Oxycodone', key: 'Oxycodone' },
                    { title: 'Oxymorphone', value: 'Oxymorphone', key: 'Oxymorphone' },
                    { title: 'Buprenorphine', value: 'Buprenorphine', key: 'Buprenorphine' },
                ],
            },
            {
                title: 'Other fully synthetic opioids',
                value: 'opioids/syntheticOpioids',
                key: 'opioids/syntheticOpioids',
                children: [
                    { title: 'Methadone', value: 'Methadone', key: 'Methadone' },
                    { title: 'Tramadol', value: 'Tramadol', key: 'Tramadol' },
                ],
            },
        ],
    },
    {
        title: 'Benzodiazepines',
        value: 'benzodiazepines',
        key: 'benzodiazepines',
        children: [
            { title: 'Alprazolam', value: 'Alprazolam', key: 'Alprazolam' },
            { title: 'Bromazepam', value: 'Bromazepam', key: 'Bromazepam' },
            { title: 'Bromazolam', value: 'Bromazolam', key: 'Bromazolam' },
            { title: 'Clonazepam', value: 'Clonazepam', key: 'Clonazepam' },
            { title: 'Desalkylgidazepam', value: 'Desalkylgidazepam', key: 'Desalkylgidazepam' },
            { title: 'Diazepam', value: 'Diazepam', key: 'Diazepam' },
            { title: 'Etizolam', value: 'Etizolam', key: 'Etizolam' },
            { title: 'Flubromazepam', value: 'Flubromazepam', key: 'Flubromazepam' },
            { title: 'Flurazepam', value: 'Flurazepam', key: 'Flurazepam' },
            { title: 'Lorazepam', value: 'Lorazepam', key: 'Lorazepam' },
            { title: 'Oxazepam', value: 'Oxazepam', key: 'Oxazepam' },
            { title: 'Temazepam', value: 'Temazepam', key: 'Temazepam' },
            { title: 'Triazolam', value: 'Triazolam', key: 'Triazolam' },
        ],
    },
    {
        title: 'Barbiturates',
        value: 'barbiturates',
        key: 'barbiturates',
        children: [
            { title: 'Barbital', value: 'Barbital', key: 'Barbital' },
            { title: 'Phenobarbital', value: 'Phenobarbital', key: 'Phenobarbital' },
        ],
    },
    {
        title: 'Stimulants',
        value: 'stimulants',
        key: 'stimulants',
        children: [
            { title: 'Methamphetamine', value: 'Methamphetamine', key: 'Methamphetamine' },
            { title: 'MDMA', value: 'MDMA', key: 'MDMA' },
            { title: 'Methylphenidate', value: 'Methylphenidate', key: 'Methylphenidate' },
            { title: 'Phenmetrazine', value: 'Phenmetrazine', key: 'Phenmetrazine' },
            { title: 'Pentylone', value: 'Pentylone', key: 'Pentylone' },
            { title: 'Tenamfetamine', value: 'Tenamfetamine', key: 'Tenamfetamine' },
            { title: 'Cocaine', value: 'Cocaine', key: 'Cocaine' },
            { title: 'Crack cocaine', value: 'Crack cocaine', key: 'Crack cocaine' },
            { title: 'Benzoylecgonine', value: 'Benzoylecgonine', key: 'Benzoylecgonine' },
            { title: 'Benzylcyclohexanamine', value: 'Benzylcyclohexanamine', key: 'Benzylcyclohexanamine' },
            { title: 'Caffeine', value: 'Caffeine', key: 'Caffeine' },
        ],
    },
    {
        title: 'Dissociatives',
        value: 'dissociatives',
        key: 'dissociatives',
        children: [
            { title: 'Ketamine', value: 'Ketamine', key: 'Ketamine' },
            { title: 'Ketamine hydrochloride', value: 'Ketamine hydrochloride', key: 'Ketamine hydrochloride' },
            { title: 'Phencyclidine', value: 'Phencyclidine', key: 'Phencyclidine' },
        ],
    },
    {
        title: 'Synthetic Cannabinoids',
        value: 'cannabinoids',
        key: 'cannabinoids',
        children: [
            { title: '5F-Apinaca', value: '5F-Apinaca', key: '5F-Apinaca' },
            { title: 'FUB-AMB', value: 'FUB-AMB', key: 'FUB-AMB' },
        ],
    },
    {
        title: 'Other Psychoactives',
        value: 'otherPsychoactives',
        key: 'otherPsychoactives',
        children: [
            { title: 'Aripiprazole', value: 'Aripiprazole', key: 'Aripiprazole' },
            { title: 'Harmine', value: 'Harmine', key: 'Harmine' },
            { title: 'Diphenhydramine', value: 'Diphenhydramine', key: 'Diphenhydramine' },
            { title: 'Detomidine', value: 'Detomidine', key: 'Detomidine' },
            { title: 'Dexmedetomidine', value: 'Dexmedetomidine', key: 'Dexmedetomidine' },
            { title: 'Dexmedetomidine hydrochloride', value: 'Dexmedetomidine hydrochloride', key: 'Dexmedetomidine hydrochloride' },
            { title: 'Medetomidine', value: 'Medetomidine', key: 'Medetomidine' },
            { title: 'Xylazine', value: 'Xylazine', key: 'Xylazine' },
            { title: 'Xylazine hydrochloride', value: 'Xylazine hydrochloride', key: 'Xylazine hydrochloride' },
            { title: 'Fluoxetine', value: 'Fluoxetine', key: 'Fluoxetine' },
            { title: 'Sertraline', value: 'Sertraline', key: 'Sertraline' },
            { title: 'Trazodone', value: 'Trazodone', key: 'Trazodone' },
            { title: 'Trimipramine', value: 'Trimipramine', key: 'Trimipramine' },
            { title: 'Tizanidine', value: 'Tizanidine', key: 'Tizanidine' },
        ],
    },
    {
        title: 'Analgesics & NSAIDs',
        value: 'analgesicsNSAIDs',
        key: 'analgesicsNSAIDs',
        children: [
            { title: 'Acetaminophen', value: 'Acetaminophen', key: 'Acetaminophen' },
            { title: 'Acetylsalicylic acid', value: 'Acetylsalicylic acid', key: 'Acetylsalicylic acid' },
            { title: 'Ibuprofen', value: 'Ibuprofen', key: 'Ibuprofen' },
            { title: 'Celecoxib', value: 'Celecoxib', key: 'Celecoxib' },
            { title: 'Ampyrone', value: 'Ampyrone', key: 'Ampyrone' },
            { title: 'Phenacetin', value: 'Phenacetin', key: 'Phenacetin' },
        ],
    },
    {
        title: 'Local Anesthetics',
        value: 'localAnesthetics',
        key: 'localAnesthetics',
        children: [
            { title: 'Benzocaine', value: 'Benzocaine', key: 'Benzocaine' },
            { title: 'Lidocaine', value: 'Lidocaine', key: 'Lidocaine' },
            { title: 'Procaine', value: 'Procaine', key: 'Procaine' },
            { title: 'Dibucaine', value: 'Dibucaine', key: 'Dibucaine' },
        ],
    },
    {
        title: 'Cardiovascular Drugs',
        value: 'cardiovascular',
        key: 'cardiovascular',
        children: [
            { title: 'Amlodipine', value: 'Amlodipine', key: 'Amlodipine' },
            { title: 'Atenolol', value: 'Atenolol', key: 'Atenolol' },
            { title: 'Metoprolol', value: 'Metoprolol', key: 'Metoprolol' },
            { title: 'Clonidine', value: 'Clonidine', key: 'Clonidine' },
            { title: 'Clonidine hydrochloride', value: 'Clonidine hydrochloride', key: 'Clonidine hydrochloride' },
            { title: 'Apixaban', value: 'Apixaban', key: 'Apixaban' },
            { title: 'Atorvastatin', value: 'Atorvastatin', key: 'Atorvastatin' },
            { title: 'Rosuvastatin', value: 'Rosuvastatin', key: 'Rosuvastatin' },
            { title: 'Simvastatin', value: 'Simvastatin', key: 'Simvastatin' },
            { title: 'Hydrochlorothiazide', value: 'Hydrochlorothiazide', key: 'Hydrochlorothiazide' },
            { title: 'Quinidine', value: 'Quinidine', key: 'Quinidine' },
            { title: 'Tadalafil', value: 'Tadalafil', key: 'Tadalafil' },
        ],
    },
    {
        title: 'Gastrointestinal Agents',
        value: 'gastrointestinal',
        key: 'gastrointestinal',
        children: [
            { title: 'Esomeprazole', value: 'Esomeprazole', key: 'Esomeprazole' },
            { title: 'Pantoprazole', value: 'Pantoprazole', key: 'Pantoprazole' },
        ],
    },
    {
        title: 'Antiinfectives',
        value: 'antiinfectives',
        key: 'antiinfectives',
        children: [
            { title: 'Dapsone', value: 'Dapsone', key: 'Dapsone' },
            { title: 'Trimethoprim', value: 'Trimethoprim', key: 'Trimethoprim' },
            { title: 'Pyrimethamine', value: 'Pyrimethamine', key: 'Pyrimethamine' },
            { title: 'Quinine', value: 'Quinine', key: 'Quinine' },
            { title: 'Hydroxychloroquine', value: 'Hydroxychloroquine', key: 'Hydroxychloroquine' },
        ],
    },
    {
        title: 'Antidiabetics',
        value: 'antidiabetics',
        key: 'antidiabetics',
        children: [
            { title: 'Metformin', value: 'Metformin', key: 'Metformin' },
            { title: 'Tolbutamine', value: 'Tolbutamine', key: 'Tolbutamine' },
        ],
    },
    {
        title: 'Vitamins & Supplements',
        value: 'vitaminsSupplements',
        key: 'vitaminsSupplements',
        children: [
            { title: 'Ascorbic acid', value: 'Ascorbic acid', key: 'Ascorbic acid' },
            { title: 'Cholecalciferol', value: 'Cholecalciferol', key: 'Cholecalciferol' },
            { title: 'Menaquinone', value: 'Menaquinone', key: 'Menaquinone' },
            { title: 'Thiamine', value: 'Thiamine', key: 'Thiamine' },
            { title: 'Creatine', value: 'Creatine', key: 'Creatine' },
            { title: 'Glucosamine', value: 'Glucosamine', key: 'Glucosamine' },
            { title: 'Melatonin', value: 'Melatonin', key: 'Melatonin' },
            { title: 'Inositol', value: 'Inositol', key: 'Inositol' },
            { title: 'myo-Inositol', value: 'myo-Inositol', key: 'myo-Inositol' },
            { title: 'Dimethyl sulfone', value: 'Dimethyl sulfone', key: 'Dimethyl sulfone' },
            { title: 'Androstanolone', value: 'Androstanolone', key: 'Androstanolone' },
        ],
    },
    {
        title: 'Amino Acids & Precursors',
        value: 'aminoAcidsPrecursors',
        key: 'aminoAcidsPrecursors',
        children: [
            { title: 'Glutamic acid', value: 'Glutamic acid', key: 'Glutamic acid' },
            { title: 'Glutamine', value: 'Glutamine', key: 'Glutamine' },
        ],
    },
    {
        title: 'Excipients & Fillers',
        value: 'excipients',
        key: 'excipients',
        children: [
            { title: 'alpha-Lactose', value: 'alpha-Lactose', key: 'alpha-Lactose' },
            { title: 'Lactose', value: 'Lactose', key: 'Lactose' },
            { title: 'Calcium carbonate', value: 'Calcium carbonate', key: 'Calcium carbonate' },
            { title: 'Calcium sulfate', value: 'Calcium sulfate', key: 'Calcium sulfate' },
            { title: 'Cellulose', value: 'Cellulose', key: 'Cellulose' },
            { title: 'Dextrose', value: 'Dextrose', key: 'Dextrose' },
            { title: 'Dextrose monohydrate', value: 'Dextrose monohydrate', key: 'Dextrose monohydrate' },
            { title: 'Maltodextrin', value: 'Maltodextrin', key: 'Maltodextrin' },
            { title: 'Dicalcium phosphate', value: 'Dicalcium phosphate', key: 'Dicalcium phosphate' },
            { title: 'Monocalcium phosphate', value: 'Monocalcium phosphate', key: 'Monocalcium phosphate' },
            { title: 'Magnesium stearate', value: 'Magnesium stearate', key: 'Magnesium stearate' },
            { title: 'Magnesium sulfate', value: 'Magnesium sulfate', key: 'Magnesium sulfate' },
            { title: 'Stearic acid', value: 'Stearic acid', key: 'Stearic acid' },
            { title: 'Sodium bicarbonate', value: 'Sodium bicarbonate', key: 'Sodium bicarbonate' },
            { title: 'Sodium metabisulfite', value: 'Sodium metabisulfite', key: 'Sodium metabisulfite' },
            { title: 'Citric acid', value: 'Citric acid', key: 'Citric acid' },
            { title: 'Malic acid', value: 'Malic acid', key: 'Malic acid' },
            { title: 'Benzoic acid', value: 'Benzoic acid', key: 'Benzoic acid' },
            { title: 'Boric acid', value: 'Boric acid', key: 'Boric acid' },
            { title: 'o-Toluic acid', value: 'o-Toluic acid', key: 'o-Toluic acid' },
        ],
    },
    {
        title: 'Food Additives',
        value: 'foodAdditives',
        key: 'foodAdditives',
        children: [
            { title: 'Aspartame', value: 'Aspartame', key: 'Aspartame' },
            { title: 'Sucralose', value: 'Sucralose', key: 'Sucralose' },
            { title: 'Saccharin', value: 'Saccharin', key: 'Saccharin' },
            { title: 'Sugar', value: 'Sugar', key: 'Sugar' },
            { title: 'Fructose', value: 'Fructose', key: 'Fructose' },
            { title: 'Maltose', value: 'Maltose', key: 'Maltose' },
            { title: 'Xylitol', value: 'Xylitol', key: 'Xylitol' },
            { title: 'Sorbitol', value: 'Sorbitol', key: 'Sorbitol' },
            { title: 'Mannitol', value: 'Mannitol', key: 'Mannitol' },
            { title: 'Erythritol', value: 'Erythritol', key: 'Erythritol' },
            { title: 'FD&C Orange', value: 'FD&C Orange', key: 'FD&C Orange' },
            { title: 'FD&C Red 40', value: 'FD&C Red 40', key: 'FD&C Red 40' },
            { title: 'FD&C Yellow 5', value: 'FD&C Yellow 5', key: 'FD&C Yellow 5' },
        ],
    },
    {
        title: 'Chemical Reagents & Precursors',
        value: 'chemicalsPrecursors',
        key: 'chemicalsPrecursors',
        children: [
            { title: '4-Chloroaniline', value: '4-Chloroaniline', key: '4-Chloroaniline' },
            { title: 'PMK methyl glycidate', value: 'PMK methyl glycidate', key: 'PMK methyl glycidate' },
        ],
    },
    {
        title: 'Pesticides, Rodenticides & Adulterants',
        value: 'pesticidesAdulterants',
        key: 'pesticidesAdulterants',
        children: [
            { title: 'Coumatetralyl', value: 'Coumatetralyl', key: 'Coumatetralyl' },
            { title: 'Levamisol', value: 'Levamisol', key: 'Levamisol' },
            { title: 'Strychnine', value: 'Strychnine', key: 'Strychnine' },
        ],
    }
];

const treeDataKeyMap = buildKeyMap(treeData);

const AllPredictionsPanel = () => {

    const [isAllPredictionsPanelOpen, setIsAllPredictionsPanelOpen] = useAtom(isAllPredictionsPanelOpenState);
    const [modalWindowState, setModalWindowState] = useState({
        width: '500px',
        height: '500px',
        x: 10,
        y: 10,
    });
    const [sortingKey, setSortingKey] = useState('maximum');
    const chartContainerRef = useRef(null);
    const rootID = useRef(Date.now().toString());
    const rootRef = useRef(null);
    const chartRef = useRef(null);
    const startEndChangeTimeoutRef = useRef(null);

    const [, setInfoModalSubstance] = useAtom(infoModalSubstanceState);
    const [, setIsSubstanceInfoModalOpen] = useAtom(isSubstanceInfoModalOpenState);
    const [isZoomed, setIsZoomed] = useState(false);
    const [map] = useAtom(mapState);
    const [traceMap] = useAtom(traceMapState);
    const [currentSerial] = useAtom(currentSerialState);
    const [loadingMap] = useAtom(loadingMapState);

    const [searchValue, setSearchValue] = useState('');
    const [selectedSubstances, setSelectedSubstances] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [filteredResults, setFilteredResults] = useState([]);

    useLayoutEffect(() => {

        if (rootRef.current) {
            rootRef.current.dispose();
        }

        let root = am5.Root.new(rootID.current);
        root.autoResize = false;
        root._logo.dispose();

        root.setThemes([
            am5themes_Animated.new(root)
        ]);

        let chart = root.container.children.push(am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            panY: false,
            wheelY: "panY",
            paddingTop: 8,
            paddingBottom: 0,
        }));

        chart.set("cursor", am5xy.XYCursor.new(root, {
            behavior: "zoomX"
        }));

        let cursor = chart.get("cursor");

        cursor.lineX.setAll({
            stroke: am5.color(0x1890ff),
            strokeWidth: 3,
            strokeDasharray: []
        });

        cursor.lineY.setAll({
            visible: false
        });

        let scrollbarY = am5.Scrollbar.new(root, {
            orientation: "vertical"
        });

        scrollbarY.startGrip.setAll({
            visible: false
        });

        scrollbarY.endGrip.setAll({
            visible: false
        });

        chart.set("scrollbarY", scrollbarY);

        let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
            categoryField: "category",
            renderer: am5xy.AxisRendererY.new(root, {
                inversed: true,
                minGridDistance: 1
            })
        }));

        let yRenderer = yAxis.get("renderer");

        yRenderer.labels.template.setAll({
            paddingRight: 30,
            interactive: true,
            cursorOverStyle: "pointer"
        });

        yRenderer.labels.template.setup = function (target) {
            target.setAll({
                cursorOverStyle: "pointer",
                background: am5.Rectangle.new(root, {
                    fill: am5.color(0x000000),
                    fillOpacity: 0
                })
            });
        };

        yRenderer.labels.template.events.on('click', function (e) {
            const substance = e.target.dataItem.dataContext.category;
            setInfoModalSubstance(substance);
            setIsSubstanceInfoModalOpen(true);
        });

        yRenderer.grid.template.setAll({
            location: 0.5
        });

        let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            max: 100,
            strictMinMax: true,
            maxPrecision: 0,
            renderer: am5xy.AxisRendererX.new(root, {
                minGridDistance: 30
            })
        }));

        xAxis.children.push(am5.Label.new(root, { text: "Signal Strength", x: am5.p50, centerX: am5.p50 }));

        let series = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            baseAxis: yAxis,
            valueXField: "signal",
            valueField: "value",
            categoryYField: "category",
            calculateAggregates: true,
        }));

        series.strokes.template.setAll({
            strokeOpacity: 0
        });

        let circleTemplate = am5.Template.new({});

        series.bullets.push(function () {
            return am5.Bullet.new(root, {
                locationY: 0.5,
                sprite: am5.Circle.new(root, {
                    radius: 5,
                    fillOpacity: 0.5,
                    fill: am5.color(0x1890ff)
                }, circleTemplate)
            });
        });

        let iqrSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            openValueXField: "q1",
            valueXField: "q3",
            categoryYField: "category",
            sequencedInterpolation: true,
        }));

        iqrSeries.columns.template.setAll({
            fill: am5.color(0x1890ff),
            fillOpacity: 0.3,
            stroke: am5.color("#fff"),
            strokeOpacity: 0,
            height: 52
        });

        let averageSeries = chart.series.push(am5xy.LineSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            baseAxis: yAxis,
            valueXField: "average",
            categoryYField: "category",
        }));

        averageSeries.strokes.template.setAll({
            strokeOpacity: 0
        });

        averageSeries.bullets.push(function () {
            let graphics = am5.Rectangle.new(root, {
                width: 2,
                height: 52,
                centerX: am5.p50,
                centerY: am5.p50,
                fill: am5.color(0x1890ff)
            });

            return am5.Bullet.new(root, {
                sprite: graphics
            });
        });

        chart.zoomOutButton.set("forceHidden", true);

        chart.events.on("click", () => {
            xAxis.zoom(0, 1);
        });

        xAxis.on("start", handleStartEndChange);
        xAxis.on("end", handleStartEndChange);

        yAxis.events.on("boundschanged", updateChartZoom);
        series.events.on("datavalidated", updateChartZoom);

        function handleStartEndChange() {
            if (startEndChangeTimeoutRef.current) {
                clearTimeout(startEndChangeTimeoutRef.current);
            }
            startEndChangeTimeoutRef.current = setTimeout(function () {
                zoomEnded();
            }, 50);
        }

        function zoomEnded() {
            setIsZoomed(!(xAxis.getPrivate("selectionMin") == 0 && xAxis.getPrivate("selectionMax") == 100));
        }

        rootRef.current = root;
        chartRef.current = chart;

        return () => {
            root.dispose();
        };
    }, []);

    useEffect(() => {
        rootRef.current.resize();
    }, [modalWindowState]);

    const results = useMemo(() => {
        if (map.length === 0 || traceMap.length === 0) return [];
        const computed = computeResults(map, traceMap, true);
        if (
            !computed?.length ||
            !computed.some(substance => substance.predictions !== null) ||
            (computed.length === 1 && computed[0].substance === 'Unknown')
        ) {
            return [];
        }
        return computed;
    }, [map, traceMap]);

    const resultWithStats = useMemo(() => {
        return results.map(result => {
            const { q1, q2, q3 } = getIQR(result.predictions);
            const average = result.predictions.reduce((sum, val) => sum + val, 0) / result.predictions.length;
            const max = Math.max(...result.predictions);
            return { ...result, q1, q2, q3, average, max };
        });
    }, [results]);

    const sortedResults = useMemo(() => {
        if (!resultWithStats.length) return [];

        return [...resultWithStats].sort((a, b) => {
            switch (sortingKey) {
                case 'median':
                    return b.q2 - a.q2;
                case 'average':
                    return b.average - a.average;
                case 'maximum':
                    return b.max - a.max;
            }
        });
    }, [resultWithStats, sortingKey]);

    const getPredictionBullets = (results) => {
        const data = [];
        for (const result of results) {
            for (const prediction of result.predictions) {
                data.push({
                    category: result.substance,
                    signal: prediction,
                    value: prediction,
                });
            }
        }
        return data;
    }

    const getIQRSeriesData = (results) => {
        return results.map(result => {
            const { q1, q2, q3 } = result;
            return { category: result.substance, q1, q2, q3 };
        });
    };

    const getAverageSeriesData = (results) => {
        return results.map(result => ({
            category: result.substance,
            average: result.average,
        }));
    };

    const updateChartZoom = () => {
        if (!chartRef?.current?.yAxes) return;
        const yAxis = chartRef.current.yAxes.getIndex(0);
        if (!yAxis || yAxis.data.length === 0) return;
        yAxis.zoomToIndexes(0, Math.min(yAxis.data.length, yAxis.height() / 60));
    };

    useEffect(() => {
        if (!chartRef.current) return;
        if (sortedResults.length === 0) { setFilteredResults([]); return; }
        setFilteredResults(selectedSubstances.length ? sortedResults.filter(r => selectedSubstances.includes(r.substance)) : sortedResults);
    }, [sortedResults, selectedSubstances]);

    useEffect(() => {
        if (!chartRef.current) return;

        const yAxis = chartRef.current.yAxes.getIndex(0);
        const series = chartRef.current.series.getIndex(0);
        const iqrSeries = chartRef.current.series.getIndex(1);
        const averageSeries = chartRef.current.series.getIndex(2);

        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const pagedResults = filteredResults.slice(startIndex, endIndex);

        const seriesBulletData = getPredictionBullets(pagedResults);
        const iqrSeriesData = getIQRSeriesData(pagedResults);
        const averageSeriesData = getAverageSeriesData(pagedResults);

        yAxis.data.setAll(pagedResults.map(row => ({ category: row.substance })));
        series.data.setAll(seriesBulletData);
        iqrSeries.data.setAll(iqrSeriesData);
        averageSeries.data.setAll(averageSeriesData);

    }, [filteredResults, currentPage, pageSize]);

    const handleReset = () => {
        if (!chartRef.current) return;
        chartRef.current.xAxes.getIndex(0).zoom(0, 1);
    }

    return (
        <>
            {isAllPredictionsPanelOpen && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    zIndex: 100,
                    pointerEvents: 'none'
                }}
                >
                    <Rnd
                        dragHandleClassName="drag-handle"
                        style={{
                            pointerEvents: 'auto',
                            borderRadius: '16px',
                            position: 'relative',
                            boxShadow: '3px 3px 10px 3px #dddddd',
                        }}
                        size={{ width: modalWindowState.width, height: modalWindowState.height }}
                        position={{ x: modalWindowState.x, y: modalWindowState.y }}
                        onDragStop={(e, d) => {
                            setModalWindowState(prev => ({ ...prev, x: d.x, y: d.y }))
                        }}
                        onResize={(e, direction, ref, delta, position) => {
                            setModalWindowState(prev => ({
                                ...prev,
                                width: ref.style.width,
                                height: ref.style.height,
                                ...position
                            }));
                        }}
                        minWidth={450}
                        minHeight={450}
                        maxWidth={900}
                        maxHeight={900}
                    >
                        <div className="drag-handle" style={{
                            zIndex: '102',
                            position: 'absolute',
                            width: '100%',
                            height: '32px',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px 16px 0 0',
                        }}>
                            <p style={{ textAlign: 'center', lineHeight: '32px', margin: '0', cursor: 'default' }}>All Predictions{currentSerial ? ` | Serial ${currentSerial}` : null}</p>
                        </div>
                        <Alert
                            message={
                                <>
                                    <ExclamationCircleFilled style={{ marginRight: '8px', color: '#FAAD14' }} />
                                    Raw ML output. Not validated. For inspection only.
                                </>
                            }
                            type="warning"
                            showIcon={false}
                            banner
                            style={{
                                width: '100%',
                                position: 'absolute',
                                top: '32px',
                                zIndex: 103,
                                padding: '4px 12px',
                                textAlign: 'center',
                            }}
                        />
                        <div
                            style={{
                                backgroundColor: '#f5f5f5',
                                position: 'relative',
                                top: '0px',
                                left: '0px',
                                width: `calc(${modalWindowState.width})`,
                                height: `calc(${modalWindowState.height})`,
                                zIndex: 101,
                                borderRadius: '16px',
                                padding: '16px'
                            }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: `calc(${modalWindowState.height} - 40px)`,
                                    marginTop: 'calc(24px + 32px)',
                                    overflow: 'hidden',
                                }}
                                className={styles.popup}
                            >
                                <Row justify="center" style={{ marginBottom: '8px' }}>
                                    <Col span={24} style={{ display: 'grid', placeItems: 'center' }}>
                                        <Radio.Group value={sortingKey} style={{ width: '100%' }}>
                                            <Radio.Button value="median" onChange={e => { setSortingKey(e.target.value) }} style={{ width: 'calc(100% / 3)', height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>Median</Radio.Button>
                                            <Radio.Button value="maximum" onChange={e => { setSortingKey(e.target.value) }} style={{ width: 'calc(100% / 3)', height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>Maximum</Radio.Button>
                                            <Radio.Button value="average" onChange={e => { setSortingKey(e.target.value) }} style={{ width: 'calc(100% / 3)', height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>Average</Radio.Button>
                                        </Radio.Group>
                                    </Col>
                                </Row>
                                <Row justify="center">
                                    <Col span={24} style={{ display: 'grid', placeItems: 'center' }}>
                                        <TreeSelect
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Search or select substances"
                                            allowClear
                                            multiple
                                            onChange={(selectedKeys) => {
                                                setCurrentPage(1);
                                                setPageSize(25);
                                                setSelectedSubstances(getSelectedLeaves(treeDataKeyMap, selectedKeys));
                                            }}
                                            treeData={treeData}
                                            onSearch={e => { setSearchValue(e); }}
                                            searchValue={searchValue}
                                            maxTagCount='responsive'
                                            maxTagTextLength={17}
                                        />
                                    </Col>
                                </Row>
                                <Row justify="center" style={{ height: 'calc(100% - 116px - 28px - 8px)', marginBottom: '8px' }}>
                                    <Col span={24} style={{ height: '100%', overflow: 'hidden' }}>
                                        {loadingMap && (
                                            <div style={{ width: '100%', margin: '8px 0' }}>
                                                {Array.from({ length: 11 }).map((_, index) => (
                                                    <Skeleton.Button
                                                        key={index}
                                                        active={true}
                                                        block={true}
                                                        style={{ width: '100%', height: '60px', marginBottom: '8px' }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <div ref={chartContainerRef} id={`${rootID.current}`} style={{ width: "100%", height: `100%` }}></div>
                                        {isZoomed && (<Button shape='circle' color="default" size="small" style={{ position: 'absolute', top: '8px', right: '60px' }} icon={<MinusOutlined />} onClick={handleReset} />)}
                                    </Col>
                                </Row>
                                <Row justify="center" style={{ height: 'auto' }}>
                                    <Col span={24} style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                                        <Pagination
                                            size="small"
                                            current={currentPage}
                                            pageSize={pageSize}
                                            total={filteredResults.length}
                                            pageSizeOptions={[25, 50, 100, 200]}
                                            showSizeChanger={filteredResults.length > 25}
                                            onChange={(page, pageSize) => {
                                                setCurrentPage(page);
                                                setPageSize(pageSize);
                                            }}
                                            showTotal={(total, range) => `${total} substances`}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <Button
                            type="primary"
                            danger
                            shape="circle"
                            size="small"
                            icon={<CloseOutlined style={{ fontSize: '9px' }} />}
                            onClick={() => {
                                setIsAllPredictionsPanelOpen(false);
                            }}
                            style={{
                                position: 'fixed',
                                top: '8px',
                                left: '16px',
                                minWidth: '16px',
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 103,
                            }}
                        />
                    </Rnd>
                </div>
            )}
        </>
    )
}

export default AllPredictionsPanel;